import { AbstractCollisionHandler } from "../../componentLibrary/AbstractCollisionHandler.js";
import { ShelfController } from "../shelves/shelfController.js";
import { ItemEntity } from "../ordermanagement/itemEntity.js";
import { InputAction } from "../../inputactionlist.js";
import { BasicLifecycle } from "../../componentLibrary/lifecycle.js";
import { DeliveryController } from "../deliveryEntity/deliveryController.js";
import { WallEntity } from "../scenes/wallEntity.js";
import { ASSET_MANAGER } from "../main.js";
import { Ball } from "../scenes/storeExterior/ballController.js";
import { Bush } from "../scenes/storeExterior/bushController.js";
import { VehicleEntity } from "../scenes/storeExterior/vehicleEntity.js";
import { SelfCheckout } from "../scenes/storeInterior/selfCheckoutController.js";
import { ShoppingCart } from "../scenes/storeInterior/shoppingCartController.js";
import { MonsterEntity } from "../monster/monsterEntity.js";
/**
 * Player collision handler that prevents the player from
 * moving through solid objects
 * @author Emma and Primo, Preston
 */
export class PlayerCollisionHandler extends AbstractCollisionHandler {
    constructor(boundingBox, movementComponent, sizeComponent, inputSys, inventoryMgr, orderLoop, healthMon) {
        super();
        this.enemyCooldownFlag = false;
        this.boundingBox = boundingBox;
        this.movementComponent = movementComponent;
        this.sizeComponent = sizeComponent;
        this.inputSys = inputSys;
        this.inventoryMgr = inventoryMgr;
        this.orderLoop = orderLoop;
        this.healthMon = healthMon;
    }
    handleCollision(other, otherBounds) {
        const pos = { x: this.movementComponent.getPosition().x, y: this.movementComponent.getPosition().y };
        const bbWidth = this.boundingBox.getRight() - this.boundingBox.getLeft();
        const bbHeight = this.boundingBox.getBottom() - this.boundingBox.getTop();
        const xOffset = this.boundingBox.getOffsetX();
        const yOffset = this.boundingBox.getOffsetY();
        const playerLeft = this.boundingBox.getLeft();
        const playerRight = this.boundingBox.getRight();
        const playerTop = this.boundingBox.getTop();
        const playerBottom = this.boundingBox.getBottom();
        // Do not allow walking through the following objects
        if (other instanceof ShelfController
            || other instanceof WallEntity
            || other instanceof Ball
            || other instanceof Bush
            || other instanceof VehicleEntity
            || other instanceof SelfCheckout
            || other instanceof ShoppingCart) {
            const othLeft = otherBounds.getLeft();
            const othRight = otherBounds.getRight();
            const othTop = otherBounds.getTop();
            const othBottom = otherBounds.getBottom();
            // Calculate overlap on each axis
            const overlapLeft = playerRight - othLeft;
            const overlapRight = othRight - playerLeft;
            const overlapTop = playerBottom - othTop;
            const overlapBottom = othBottom - playerTop;
            // Find the smallest overlap to determine which side to push out from
            const minOverlapX = Math.min(overlapLeft, overlapRight);
            const minOverlapY = Math.min(overlapTop, overlapBottom);
            // Push the player out on the axis with smallest penetration
            if (minOverlapX < minOverlapY) {
                // Resolve on X axis
                if (overlapLeft < overlapRight) {
                    // Push player to the left
                    pos.x = othLeft - (xOffset + bbWidth);
                }
                else {
                    // Push player to the right
                    pos.x = othRight - xOffset;
                }
            }
            else {
                // Resolve on Y axis
                if (overlapTop < overlapBottom) {
                    // Push player up
                    pos.y = othTop - (yOffset + bbHeight);
                }
                else {
                    // Push player down
                    pos.y = othBottom - yOffset;
                }
            }
            this.movementComponent.setPosition(pos);
        }
        // handle item collisions and pickups
        if (other instanceof ItemEntity) {
            const item = other;
            const itemType = item.getItemType();
            if (this.inputSys.isActionActiveSingle(InputAction.PICK_UP)) {
                //console.debug("Picking up " + itemType);
                // Pickup component and remove the component from the canvas
                this.inventoryMgr.addItem(itemType).then(function () {
                    var _a;
                    // Play pickup sound
                    ASSET_MANAGER.playMusic("itemPickup");
                    // promise resolved, remove the item from the shelf
                    (_a = item.getComponent(BasicLifecycle)) === null || _a === void 0 ? void 0 : _a.die();
                }, function (reject) { });
            }
        }
        // Handle item delivery
        if (other instanceof DeliveryController) {
            const customer = other;
            if (this.inputSys.isActionActiveSingle(InputAction.FULFILL)) {
                const currentItems = this.inventoryMgr.getAllItems();
                if (currentItems.size > 0 && this.orderLoop.getCurrentActiveOrder()) {
                    this.orderLoop.deliverOrder(currentItems);
                    this.inventoryMgr.clearItems();
                }
            }
        }
        if (other instanceof MonsterEntity) {
            const enemyCoolTimeMs = 1000;
            if (!this.enemyCooldownFlag) {
                const health = this.healthMon.damage(); // damage and return new health value
                const speedBias = health / this.healthMon.getMaxHealth();
                this.movementComponent.setSpeedBias(speedBias);
                this.enemyCooldownFlag = true;
                // to avoid spamming to 0 on collision, use a cooldown
                setTimeout(() => {
                    this.enemyCooldownFlag = false;
                }, enemyCoolTimeMs);
            }
        }
    }
}
